import {
    buildDynamicWidgetBindingSkeleton,
    type DynamicWidgetSkeletonNode,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_binding_skeleton';
import {
    type DynamicWidgetIssue,
    type DynamicWidgetModel,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';

/*
 * Copyable debug snapshot for one dynamic widget: the validated model, its
 * binding tree, and its issues. It is sanitized and bounded on purpose — a
 * snapshot is pasted into tickets and chat, so it must never carry raw
 * exceptions, credentials, or an unbounded tool payload.
 */

export const DYNAMIC_WIDGET_SNAPSHOT_RAW_VALUE_MAX_CHARS = 200;
export const DYNAMIC_WIDGET_INSPECTOR_RAW_VALUE_MAX_CHARS = 2_000;
export const DYNAMIC_WIDGET_SNAPSHOT_MAX_BINDINGS = 200;
export const DYNAMIC_WIDGET_SNAPSHOT_MAX_ISSUES = 50;
export const DYNAMIC_WIDGET_SNAPSHOT_MAX_SKELETON_NODES = 400;
export const DYNAMIC_WIDGET_SNAPSHOT_MAX_SERIALIZED_BYTES = 64 * 1024;

// Key names that must never leave the browser in a pasted snapshot. Matched
// case-insensitively against object keys inside raw values and issue details.
// `stack` and `exception` are here because the error catalog owns user-facing
// copy — a raw exception is never part of the record.
const DISALLOWED_KEY_PATTERN =
    /(authorization|bearer|cookie|credential|password|secret|token|api[_-]?key|session[_-]?id|exception|stack)/i;

const REDACTED = '[redacted]';
const MAX_SANITIZE_DEPTH = 6;

export function isDisallowedSnapshotKey(key: string): boolean {
    return DISALLOWED_KEY_PATTERN.test(key);
}

function sanitizeValue(value: unknown, depth: number): unknown {
    if (depth > MAX_SANITIZE_DEPTH) {
        return REDACTED;
    }
    if (Array.isArray(value)) {
        return value.map((item) => sanitizeValue(item, depth + 1));
    }
    if (typeof value === 'object' && value !== null) {
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>).map(
                ([key, entry]) => [
                    key,
                    isDisallowedSnapshotKey(key)
                        ? REDACTED
                        : sanitizeValue(entry, depth + 1),
                ],
            ),
        );
    }
    return value;
}

function truncate(value: string, maxChars: number): string {
    return value.length <= maxChars ? value : `${value.slice(0, maxChars)}…`;
}

/**
 * Human-readable, sanitized, length-capped preview of a binding's raw value.
 * The inspector shows a larger cap than the copyable snapshot.
 */
export function formatDynamicWidgetRawValue(
    value: unknown,
    maxChars: number,
): string {
    if (value === undefined) {
        return 'undefined';
    }
    const sanitized = sanitizeValue(value, 0);
    const serialized =
        typeof sanitized === 'string'
            ? sanitized
            : (JSON.stringify(sanitized) ?? String(sanitized));
    return truncate(serialized, maxChars);
}

function sanitizeIssueDetails(
    issue: DynamicWidgetIssue,
): Record<string, string | number | boolean | null> {
    return Object.fromEntries(
        Object.entries(issue.safeDetails).map(([key, value]) => [
            key,
            isDisallowedSnapshotKey(key) ? REDACTED : value,
        ]),
    );
}

export type DynamicWidgetDebugSnapshot = {
    readonly schemaVersion: 1;
    readonly widgetId: string;
    readonly template: DynamicWidgetModel['template'];
    readonly templateVersion: number;
    readonly sources: ReadonlyArray<Record<string, unknown>>;
    readonly bindings: ReadonlyArray<{
        readonly id: string;
        readonly kind: string;
        readonly sourceIds: ReadonlyArray<string>;
        readonly paths: ReadonlyArray<string>;
        readonly transform: string | null;
        readonly rawValuePreview: string;
        readonly renderedValue: string | null;
        readonly freshness: string;
    }>;
    readonly skeleton: ReadonlyArray<DynamicWidgetSkeletonNode>;
    readonly issues: ReadonlyArray<{
        readonly id: string;
        readonly code: DynamicWidgetIssue['code'];
        readonly level: DynamicWidgetIssue['level'];
        readonly scope: DynamicWidgetIssue['scope'];
        readonly bindingIds: ReadonlyArray<string>;
        readonly sourceIds: ReadonlyArray<string>;
        readonly retryable: boolean;
        readonly safeDetails: Record<string, string | number | boolean | null>;
    }>;
    readonly truncated: boolean;
};

export function buildDynamicWidgetDebugSnapshot(
    model: DynamicWidgetModel,
): DynamicWidgetDebugSnapshot {
    const bindings = model.bindings
        .slice(0, DYNAMIC_WIDGET_SNAPSHOT_MAX_BINDINGS)
        .map((binding) => ({
            id: binding.id,
            kind: binding.kind,
            sourceIds: [...binding.sourceIds],
            paths: [...binding.paths],
            transform: binding.transform,
            rawValuePreview: formatDynamicWidgetRawValue(
                binding.rawValue,
                DYNAMIC_WIDGET_SNAPSHOT_RAW_VALUE_MAX_CHARS,
            ),
            renderedValue:
                binding.renderedValue === null
                    ? null
                    : truncate(
                          binding.renderedValue,
                          DYNAMIC_WIDGET_SNAPSHOT_RAW_VALUE_MAX_CHARS,
                      ),
            freshness: binding.freshness,
        }));
    const issues = model.issues
        .slice(0, DYNAMIC_WIDGET_SNAPSHOT_MAX_ISSUES)
        .map((issue) => ({
            id: issue.id,
            code: issue.code,
            level: issue.level,
            scope: issue.scope,
            bindingIds: [...issue.bindingIds],
            sourceIds: [...issue.sourceIds],
            retryable: issue.retryable,
            safeDetails: sanitizeIssueDetails(issue),
        }));
    const skeleton = buildDynamicWidgetBindingSkeleton(model).slice(
        0,
        DYNAMIC_WIDGET_SNAPSHOT_MAX_SKELETON_NODES,
    );

    let snapshot: DynamicWidgetDebugSnapshot = {
        schemaVersion: 1,
        widgetId: model.widgetId,
        template: model.template,
        templateVersion: model.templateVersion,
        // Sources are already an allowlisted, provenance-only union: tool
        // name, IDs, phase, timestamps, organization, mode.
        sources: model.sources.map(
            (source) => sanitizeValue(source, 0) as Record<string, unknown>,
        ),
        bindings,
        skeleton,
        issues,
        truncated:
            model.bindings.length > bindings.length ||
            model.issues.length > issues.length,
    };

    // Last-resort size guard: drop bindings from the tail until the serialized
    // snapshot fits, so a pathological payload can never produce a giant paste.
    let serializedBytes = new TextEncoder().encode(
        JSON.stringify(snapshot),
    ).length;
    let keptBindings = snapshot.bindings.length;
    while (
        serializedBytes > DYNAMIC_WIDGET_SNAPSHOT_MAX_SERIALIZED_BYTES &&
        keptBindings > 0
    ) {
        keptBindings = Math.floor(keptBindings / 2);
        snapshot = {
            ...snapshot,
            bindings: snapshot.bindings.slice(0, keptBindings),
            truncated: true,
        };
        serializedBytes = new TextEncoder().encode(
            JSON.stringify(snapshot),
        ).length;
    }
    return snapshot;
}

export function serializeDynamicWidgetDebugSnapshot(
    model: DynamicWidgetModel,
): string {
    return JSON.stringify(buildDynamicWidgetDebugSnapshot(model), null, 4);
}
